/**
 * ChipCancellationBatchBuilder - Etapa 1: montar o lote de cancelamento (pré-solicitação).
 *
 * Esquerda: pesquisar (Enter adiciona; bom para leitor de código de barras), colar lista
 * ou importar arquivo. Direita: lista do lote com contador, busca, remoção e recusados.
 * A validação acontece no servidor a cada inclusão; ao salvar, uma ação vale para todos
 * os chips e o servidor cria um lote por broker.
 *
 * RELEASE: 2026-10-14 v1.1.0 - Lista do lote com rolagem otimizada (desenha só as linhas visíveis), sem limite de 300.
 * RELEASE: 2026-10-14 v1.0.0 - Versão inicial.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Download, Loader2, Search, Trash2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  CANCEL_MAX_CHIPS, groupBySupplier, mergeCandidates, useChipCancelMutations, useChipCancelSearch, useValidateChipValues,
  type CancelCandidate, type CancelRejected,
} from '@/hooks/useStockChipCancellations';
import { buildCsvContent, triggerDownload } from '@/lib/export/writers';
import { ChipValuesInput } from './ChipValuesInput';

/** Altura fixa da linha e folga da rolagem otimizada (lista leve com milhares de chips). */
const ROW_H = 32;
const OVERSCAN = 10;

/** Janela de linhas visíveis a partir da rolagem (cálculo puro, testável). */
export function visibleWindow(scrollTop: number, viewport: number, total: number, rowH = ROW_H, overscan = OVERSCAN) {
  const start = Math.max(0, Math.floor(scrollTop / rowH) - overscan);
  const end = Math.min(total, Math.ceil((scrollTop + viewport) / rowH) + overscan);
  return { start, end, top: start * rowH, bottom: Math.max(0, (total - end) * rowH) };
}

export function ChipCancellationBatchBuilder({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [list, setList] = useState<CancelCandidate[]>([]);
  const [rejected, setRejected] = useState<CancelRejected[]>([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [scroll, setScroll] = useState({ top: 0, height: 600 });
  // Pesquisa com atraso curto para não consultar a cada tecla
  const [debounced, setDebounced] = useState('');
  useEffect(() => { const t = setTimeout(() => setDebounced(search), 250); return () => clearTimeout(t); }, [search]);
  const { data: results = [], isFetching } = useChipCancelSearch(debounced);
  const validate = useValidateChipValues();
  const { createBatches } = useChipCancelMutations();

  const inList = useMemo(() => new Set(list.map((c) => c.chip_id)), [list]);
  const groups = useMemo(() => groupBySupplier(list), [list]);
  const filtered = useMemo(() => {
    const f = filter.trim();
    return f ? list.filter((c) => c.iccid.includes(f) || (c.line_number ?? '').includes(f)) : list;
  }, [list, filter]);
  const win = visibleWindow(scroll.top, scroll.height, filtered.length);

  const reset = () => { setList([]); setRejected([]); setSearch(''); setFilter(''); setProgress(null); };
  const close = (o: boolean) => {
    if (!o && list.length > 0 && !createBatches.isSuccess && !window.confirm('Descartar a lista montada?')) return;
    if (!o) reset();
    onOpenChange(o);
  };

  /** Adiciona aceitos sem passar do limite. */
  const addAccepted = (incoming: CancelCandidate[]) => {
    const room = CANCEL_MAX_CHIPS - list.length;
    const { list: next, added } = mergeCandidates(list, incoming.slice(0, Math.max(room, 0)));
    setList(next);
    if (incoming.length > room) toast.warning(`Limite de ${CANCEL_MAX_CHIPS.toLocaleString('pt-BR')} chips por lote atingido`);
    return added;
  };

  const addValues = async (values: string[]) => {
    setProgress({ done: 0, total: values.length });
    try {
      const r = await validate(values, (done, total) => setProgress({ done, total }));
      const added = addAccepted(r.accepted);
      setRejected((prev) => [...r.rejected, ...prev].slice(0, CANCEL_MAX_CHIPS));
      toast.success(`${added} adicionado(s)${r.rejected.length ? ` · ${r.rejected.length} recusado(s)` : ''}`);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setProgress(null);
    }
  };

  /** Enter: adiciona o primeiro resultado ou valida o texto digitado (leitor de código). */
  const onSearchEnter = async () => {
    const v = search.trim();
    if (!v) return;
    const exact = results.find((r) => r.iccid === v || r.line_number === v) ?? (results.length === 1 ? results[0] : null);
    if (exact) { addAccepted([exact]); setSearch(''); return; }
    await addValues([v]);
    setSearch('');
    searchRef.current?.focus();
  };

  const save = () => {
    createBatches.mutate({ chipIds: list.map((c) => c.chip_id) }, { onSuccess: () => { reset(); onOpenChange(false); } });
  };

  const downloadRejected = () => {
    const csv = buildCsvContent(rejected.map((r) => ({ Valor: r.value, ICCID: r.iccid ?? '', Motivo: r.reason })));
    triggerDownload(new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' }), 'chips_recusados.csv');
  };

  const busy = !!progress || createBatches.isPending;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="flex h-[100dvh] max-h-[100dvh] w-full max-w-full flex-col gap-0 overflow-hidden p-0 sm:h-[88dvh] sm:max-w-6xl sm:rounded-lg">
        <DialogHeader className="shrink-0 border-b px-5 py-4 text-left">
          <DialogTitle className="text-base">Nova solicitação de cancelamento</DialogTitle>
          <DialogDescription className="text-xs">
            Adicione os chips e salve. A ação vale para todos; o sistema separa um lote por broker. Só entram chips em endereço de estoque e fora de kit.
          </DialogDescription>
        </DialogHeader>

        <div className="grid min-h-0 flex-1 gap-0 overflow-y-auto lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:overflow-hidden">
          {/* Coluna esquerda: formas de adicionar */}
          <section className="space-y-5 border-b p-5 lg:overflow-y-auto lg:border-b-0 lg:border-r">
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Pesquisar chip</h3>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input ref={searchRef} autoFocus value={search} maxLength={60} disabled={busy} className="pl-8"
                  placeholder="ICCID ou linha (Enter adiciona)"
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSearchEnter(); } }} />
                {isFetching && <Loader2 className="absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-muted-foreground" />}
              </div>
              {debounced.trim().length >= 3 && (
                <div className="max-h-56 overflow-y-auto rounded-md border">
                  {results.length === 0 && !isFetching && <p className="p-3 text-xs text-muted-foreground">Nenhum chip elegível encontrado.</p>}
                  {results.map((r) => (
                    <button key={r.chip_id} type="button" disabled={inList.has(r.chip_id)}
                      onClick={() => { addAccepted([r]); setSearch(''); searchRef.current?.focus(); }}
                      className="flex w-full items-center justify-between gap-2 border-b px-3 py-2 text-left text-xs last:border-b-0 hover:bg-muted/50 disabled:opacity-50">
                      <span className="min-w-0">
                        <span className="block truncate font-mono">{r.iccid}</span>
                        <span className="block truncate text-muted-foreground">{[r.line_number, r.supplier_name, r.location_name].filter(Boolean).join(' · ')}</span>
                      </span>
                      <span className="shrink-0 text-muted-foreground">{inList.has(r.chip_id) ? 'Na lista' : 'Adicionar'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Colar lista ou importar arquivo</h3>
              <ChipValuesInput onSubmit={addValues} busy={busy} />
            </div>

            {progress && (
              <div className="space-y-1">
                <Progress value={(progress.done / Math.max(progress.total, 1)) * 100} />
                <p className="text-[11px] text-muted-foreground">Validando {progress.done.toLocaleString('pt-BR')} de {progress.total.toLocaleString('pt-BR')}…</p>
              </div>
            )}

            {rejected.length > 0 && (
              <div className="space-y-2 rounded-md border-l-2 border-destructive pl-3">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="flex items-center gap-1.5 text-sm font-semibold"><AlertTriangle className="h-4 w-4 text-destructive" />{rejected.length.toLocaleString('pt-BR')} recusado(s)</h3>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={downloadRejected}><Download className="mr-1 h-3.5 w-3.5" />Baixar</Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setRejected([])}><X className="mr-1 h-3.5 w-3.5" />Limpar</Button>
                  </div>
                </div>
                <ul className="max-h-40 space-y-1 overflow-y-auto text-xs">
                  {rejected.slice(0, 100).map((r, i) => (
                    <li key={`${r.value}-${i}`} className="flex justify-between gap-2"><span className="truncate font-mono">{r.value}</span><span className="shrink-0 text-muted-foreground">{r.reason}</span></li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          {/* Coluna direita: lista do lote */}
          <section className="flex min-h-[320px] flex-col lg:min-h-0">
            <div className="shrink-0 space-y-2 border-b p-4">
              <div className="flex items-baseline justify-between gap-2">
                <p className="text-2xl font-semibold tabular-nums">{list.length.toLocaleString('pt-BR')} <span className="text-sm font-normal text-muted-foreground">chip(s) no lote</span></p>
                {list.length > 0 && <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={busy} onClick={() => window.confirm('Limpar a lista?') && setList([])}><Trash2 className="mr-1 h-3.5 w-3.5" />Limpar</Button>}
              </div>
              {groups.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {groups.map((g) => <Badge key={g.supplier_person_id} variant="secondary" className="text-[11px]">{g.supplier_name}: {g.total.toLocaleString('pt-BR')}</Badge>)}
                </div>
              )}
              {list.length > 0 && <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Buscar dentro do lote" className="h-8 text-xs" maxLength={60} />}
            </div>
            <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto"
              onScroll={(e) => setScroll({ top: e.currentTarget.scrollTop, height: e.currentTarget.clientHeight })}>
              {list.length === 0 ? (
                <p className="p-8 text-center text-sm text-muted-foreground">Nenhum chip adicionado ainda.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-background text-left text-muted-foreground">
                    <tr className="border-b"><th className="px-4 py-2 font-medium">ICCID</th><th className="px-2 py-2 font-medium">Linha</th><th className="px-2 py-2 font-medium">Broker</th><th className="w-10" /></tr>
                  </thead>
                  <tbody>
                    {win.top > 0 && <tr style={{ height: win.top }} aria-hidden />}
                    {filtered.slice(win.start, win.end).map((c) => (
                      <tr key={c.chip_id} className="border-b last:border-b-0" style={{ height: ROW_H }}>
                        <td className="px-4 py-1.5 font-mono">{c.iccid}</td>
                        <td className="px-2 py-1.5">{c.line_number ?? '—'}</td>
                        <td className="truncate px-2 py-1.5 text-muted-foreground">{c.supplier_name}</td>
                        <td className="px-2"><Button size="icon" variant="ghost" className="h-6 w-6" aria-label="Remover" disabled={busy}
                          onClick={() => setList((l) => l.filter((x) => x.chip_id !== c.chip_id))}><X className="h-3.5 w-3.5" /></Button></td>
                      </tr>
                    ))}
                    {win.bottom > 0 && <tr style={{ height: win.bottom }} aria-hidden />}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        </div>

        {/* Rodapé fixo: uma ação para todos os chips */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-t px-5 py-3">
          <p className="text-xs text-muted-foreground">{groups.length > 1 ? `Serão criados ${groups.length} lotes (um por broker).` : 'Os chips ficarão em pré-solicitação até gerar o protocolo.'}</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => close(false)} disabled={createBatches.isPending}>Cancelar</Button>
            <Button onClick={save} disabled={list.length === 0 || busy}>
              {createBatches.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Salvar {list.length ? list.length.toLocaleString('pt-BR') : ''} como pré-solicitação
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
